import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Clock, User, ArrowLeft, Share2, Bookmark, CheckCircle, Stethoscope, Building, Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import Sidebar from "@/components/Sidebar";
import SEOHead from "@/components/SEOHead";
import ShareButtons from "@/components/ShareButtons";
import BookmarkButton from "@/components/BookmarkButton";
import ReactionBar from "@/components/ReactionBar";
import CommentSection from "@/components/CommentSection";
import DOMPurify from "dompurify";
import api from "@/lib/api";

export default function ArticlePage() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiSummary, setAiSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setAiSummary(null);
      try {
        const res = await api.get(`/articles/${id}`);
        setArticle(res.data);
        // If article already has a cached AI summary, show it
        if (res.data.ai_summary) {
          setAiSummary(res.data.ai_summary);
        }
        // Track view
        api.post(`/articles/${id}/view`).catch(() => {});
        if (res.data.category_id) {
          const relRes = await api.get(`/articles?category_id=${res.data.category_id}&limit=4`);
          setRelated((relRes.data.articles || []).filter(a => a.id !== id));
        }
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
    window.scrollTo(0, 0);
  }, [id]);

  const generateAiSummary = async () => {
    setSummaryLoading(true);
    try {
      const res = await api.post(`/articles/${id}/ai-summary`);
      setAiSummary(res.data.summary);
    } catch (e) {
      console.error("AI summary failed:", e);
    }
    setSummaryLoading(false);
  };

  const sanitizedContent = useMemo(() => {
    if (!article?.content) return "";
    // Check if content looks like HTML
    if (article.content.includes("<") && article.content.includes(">")) {
      return DOMPurify.sanitize(article.content, {
        ALLOWED_TAGS: ["h1","h2","h3","h4","p","br","strong","em","u","a","ul","ol","li","blockquote","hr","img","code","pre","span","div","table","thead","tbody","tr","th","td"],
        ALLOWED_ATTR: ["href","src","alt","class","target","rel"],
      });
    }
    // Plain text - convert paragraphs
    return article.content.split("\n\n").map(p => `<p>${p.replace(/\n/g, "<br/>")}</p>`).join("");
  }, [article?.content]);

  const categoryName = article?.category_id?.replace("cat-", "").replace(/-/g, " ") || "Article";

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Skeleton className="h-8 w-96 mb-4" />
        <Skeleton className="h-[400px] w-full mb-8" />
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
        <SEOHead title="Article Not Found" />
        <h1 className="text-3xl font-serif text-foreground">Article not found</h1>
        <Link to="/" className="text-[#1E88E5] font-sans text-sm mt-4 block">Return to home</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12" data-testid="article-page">
      <SEOHead
        title={article.title}
        description={article.summary}
        image={article.featured_image}
        type="article"
        articleId={article.id}
      />

      <Link to="/" className="inline-flex items-center gap-2 text-sm font-sans text-muted-foreground hover:text-foreground mb-6" data-testid="back-home">
        <ArrowLeft className="h-4 w-4" /> Back to Home
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
        <article className="lg:col-span-8">
          <Badge className="bg-[#1E88E5] text-white border-none text-xs font-sans uppercase tracking-wider mb-4">
            {categoryName}
          </Badge>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-foreground leading-tight mb-6">
            {article.title}
          </h1>

          <div className="flex items-center gap-4 text-sm font-sans text-muted-foreground mb-6">
            {article.author_image ? <img src={article.author_image} alt="" className="w-10 h-10 rounded-full object-cover" /> : null}
            <div>
              {article.author_name && <p className="font-medium text-foreground">{article.author_name}</p>}
              <div className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{article.reading_time}</span>
                <span>{new Date(article.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
              </div>
            </div>
          </div>

          {/* Share & Bookmark bar */}
          <div className="flex items-center gap-2 mb-8">
            <ShareButtons title={article.title} summary={article.summary} />
            <BookmarkButton articleId={article.id} />
          </div>

          {/* AI Summary Section */}
          <div className="mb-8" data-testid="ai-summary-section">
            {aiSummary ? (
              <div className="relative bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-slate-800 dark:to-slate-800/80 border border-blue-200 dark:border-blue-800/40 p-5 rounded-sm">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="h-4 w-4 text-[#1E88E5]" />
                  <span className="text-xs font-sans font-bold uppercase tracking-wider text-[#1E88E5]">AI-Generated Summary</span>
                </div>
                <p className="text-sm font-sans text-slate-700 dark:text-slate-300 leading-relaxed" data-testid="ai-summary-text">
                  {aiSummary}
                </p>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={generateAiSummary}
                disabled={summaryLoading}
                className="text-xs font-sans border-[#1E88E5]/30 text-[#1E88E5] hover:bg-[#1E88E5]/5 hover:text-[#1565C0]"
                data-testid="generate-ai-summary-btn"
              >
                {summaryLoading ? (
                  <><Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> Generating AI Summary...</>
                ) : (
                  <><Sparkles className="h-3 w-3 mr-1.5" /> Generate AI Summary</>
                )}
              </Button>
            )}
          </div>

          {article.featured_image && (
            <img src={article.featured_image} alt={article.title} className="w-full aspect-[16/9] object-cover border border-slate-200 dark:border-slate-700 mb-8" />
          )}

          {article.summary && (
            <div className="bg-slate-50 dark:bg-slate-800 border-l-4 border-[#1E88E5] p-6 mb-8">
              <p className="text-sm font-sans text-muted-foreground italic leading-relaxed">{article.summary}</p>
            </div>
          )}

          {/* Article content - supports HTML from WYSIWYG or plain text */}
          <div
            className="prose prose-lg max-w-none font-sans text-foreground leading-relaxed
              [&_h2]:text-2xl [&_h2]:font-serif [&_h2]:font-bold [&_h2]:mt-8 [&_h2]:mb-4 [&_h2]:text-foreground
              [&_h3]:text-xl [&_h3]:font-serif [&_h3]:font-bold [&_h3]:mt-6 [&_h3]:mb-3 [&_h3]:text-foreground
              [&_p]:mb-5 [&_p]:text-base [&_p]:text-slate-700 [&_p]:dark:text-slate-300 [&_p]:leading-relaxed
              [&_blockquote]:border-l-4 [&_blockquote]:border-[#1E88E5] [&_blockquote]:pl-6 [&_blockquote]:italic [&_blockquote]:text-muted-foreground [&_blockquote]:my-6
              [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-4
              [&_li]:mb-2 [&_li]:text-slate-700 [&_li]:dark:text-slate-300
              [&_a]:text-[#1E88E5] [&_a]:underline [&_a]:hover:text-[#1565C0]
              [&_strong]:font-semibold [&_strong]:text-foreground
              [&_hr]:my-8 [&_hr]:border-slate-200 [&_hr]:dark:border-slate-700
              [&_img]:max-w-full [&_img]:rounded [&_img]:my-6"
            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
            data-testid="article-content-body"
          />

          {/* Myth vs Fact display */}
          {article.is_myth_fact && (
            <div className="mt-8 space-y-4">
              {article.myth_text && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-6">
                  <span className="text-xs font-bold uppercase tracking-wider text-red-600 font-sans">Myth</span>
                  <p className="mt-2 font-sans text-foreground">{article.myth_text}</p>
                </div>
              )}
              {article.fact_text && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-6">
                  <span className="text-xs font-bold uppercase tracking-wider text-green-600 font-sans">Fact</span>
                  <p className="mt-2 font-sans text-foreground">{article.fact_text}</p>
                </div>
              )}
            </div>
          )}

          {/* Reactions */}
          <div className="mt-10">
            <ReactionBar articleId={article.id} />
          </div>

          {/* Author Bio Section */}
          {article.author_name && (article.author_bio || article.author_specialization) && (
            <>
              <Separator className="my-12" />
              <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 p-8" data-testid="author-bio-section">
                <div className="flex items-start gap-5">
                  {article.author_image && (
                    <img src={article.author_image} alt={article.author_name} className="w-20 h-20 rounded-full object-cover border-2 border-slate-200 dark:border-slate-600 shrink-0" />
                  )}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-serif font-bold text-xl text-foreground">{article.author_name}</h3>
                      {article.author_is_verified && (
                        <Badge className="bg-[#26A69A] text-white border-none text-[10px] font-sans">
                          <CheckCircle className="h-3 w-3 mr-1" /> Verified
                        </Badge>
                      )}
                    </div>
                    {article.author_specialization && (
                      <p className="text-sm font-sans text-[#1E88E5] mt-1 flex items-center gap-1">
                        <Stethoscope className="h-3 w-3" /> {article.author_specialization}
                      </p>
                    )}
                    {article.author_hospital && (
                      <p className="text-xs font-sans text-muted-foreground mt-1 flex items-center gap-1">
                        <Building className="h-3 w-3" /> {article.author_hospital}
                      </p>
                    )}
                    {article.author_bio && (
                      <p className="text-sm font-sans text-muted-foreground mt-3 leading-relaxed">{article.author_bio}</p>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          <Separator className="my-12" />

          {/* Related Articles */}
          {related.length > 0 && (
            <div>
              <h3 className="text-2xl font-serif font-bold text-foreground mb-6">Related Articles</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {related.slice(0, 3).map((art) => (
                  <Link key={art.id} to={`/article/${art.id}`} className="group" data-testid={`related-article-${art.id}`}>
                    {art.featured_image && <img src={art.featured_image} alt="" className="w-full aspect-[4/3] object-cover border border-slate-200 dark:border-slate-700 mb-3" loading="lazy" />}
                    <h4 className="font-serif font-bold text-foreground group-hover:text-[#1E88E5] transition-colors line-clamp-2">{art.title}</h4>
                    <span className="text-xs font-sans text-muted-foreground">{art.reading_time}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Comments */}
          <Separator className="my-12" />
          <CommentSection articleId={article.id} />
        </article>

        <div className="hidden lg:block lg:col-span-4">
          <div className="sticky top-24">
            <Sidebar />
          </div>
        </div>
      </div>
    </div>
  );
}
